import React, { useCallback, useEffect, useRef, useState } from 'react';
import { planApi, formatBytes, formatDateTime } from './planApi';
import { Badge, Modal } from './PlanUi';

// Admin → Plans → Partner usage: what every partner uses against their plan, per restaurant.
// Values come from stored usage (refreshed on every change and by the hourly recount);
// "Show live counts" and "Recount now" read the database directly.

const STATE_TONE = { over: 'bad', full: 'bad', near: 'warn', ok: 'good' };
const STATE_LABEL = { over: 'Over limit', full: 'At limit', near: 'Near limit', ok: 'OK' };

const fmt = (unit, n) => (n === null || n === undefined ? '—' : unit === 'bytes' ? formatBytes(n) : Number(n).toLocaleString('en-IN'));
const limitText = (m) => (m.unlimited ? 'Unlimited' : m.max === null || m.max === undefined ? '—' : fmt(m.unit, m.max));

const Meter = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="pm-muted">—</span>;
  const tone = pct > 100 ? 'bad' : pct >= 100 ? 'bad' : pct >= 80 ? 'warn' : 'good';
  return (
    <span className="pu-meter" title={`${pct}%`} aria-label={`${pct}% used`}>
      <span className={`pu-meter__fill pu-meter__fill--${tone}`} style={{ width: `${Math.min(100, pct)}%` }} />
      <span className="pu-meter__pct">{pct > 100 ? '>100' : pct}%</span>
    </span>
  );
};

// ─── Detail ──────────────────────────────────────────────────────────────────
const UsageDetail = ({ ownerId, onClose, notify, onRecounted }) => {
  const [data, setData] = useState(null);
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});
  const [error, setError] = useState('');

  const load = useCallback(async (useLive) => {
    setError('');
    try { setData(await planApi.usageDetail(ownerId, useLive)); } catch (err) { setError(err.message); }
  }, [ownerId]);
  useEffect(() => { load(live); }, [load, live]);

  const recount = async () => {
    setBusy(true);
    try {
      const r = await planApi.usageRecount(ownerId);
      notify(r.drift.length ? `Recounted — corrected ${r.drift.length} value${r.drift.length === 1 ? '' : 's'}` : 'Recounted — everything matched');
      await load(live);
      onRecounted?.();
    } catch (err) { notify(err.message, 'error'); } finally { setBusy(false); }
  };

  return (
    <Modal
      wide
      title={data ? (data.owner.name || data.owner.email) : 'Partner usage'}
      subtitle={data ? `${data.owner.email} · ${data.plan ? `${data.plan.name} (${data.plan.status.replace(/_/g, ' ')})` : 'No plan'} · ${data.restaurants.length} restaurant${data.restaurants.length === 1 ? '' : 's'}` : 'Loading…'}
      onClose={onClose}
      footer={
        <>
          <span className="pm-muted pm-small" style={{ marginRight: 'auto' }}>
            {data?.source === 'live' ? 'Live counts' : `Stored usage · last full recount ${data?.lastRecountAt ? formatDateTime(data.lastRecountAt) : 'not yet'}`}
          </span>
          <label className="pm-check"><input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} /> Show live counts</label>
          <button className="pm-btn pm-btn--primary" onClick={recount} disabled={busy}>{busy ? 'Recounting…' : 'Recount now'}</button>
        </>
      }
    >
      {error && <div className="pm-callout pm-callout--error">{error}</div>}
      {!data && !error && <p className="pm-muted">Loading…</p>}
      {data && (
        <>
          <h4 className="pu-h">Limits</h4>
          <div className="pm-table-wrap">
            <table className="pm-table pu-table">
              <thead><tr><th>Metric</th><th>Used</th><th>Plan limit</th><th>Usage</th><th>Status</th></tr></thead>
              <tbody>
                {data.metrics.map((m) => (
                  <React.Fragment key={m.key}>
                    <tr>
                      <td>
                        {m.label}
                        <div className="pm-muted pm-small">
                          {m.scope === 'restaurant' ? 'per restaurant' : 'whole account'}
                          {m.scope === 'restaurant' && m.perRestaurant?.length > 1 && (
                            <> · <button type="button" className="pu-link" onClick={() => setOpen((o) => ({ ...o, [m.key]: !o[m.key] }))}>{open[m.key] ? 'Hide restaurants' : `${m.perRestaurant.length} restaurants`}</button></>
                          )}
                        </div>
                      </td>
                      <td>
                        {fmt(m.unit, m.used)}
                        {m.scope === 'restaurant' && m.peakRestaurant && m.perRestaurant?.length > 1 && <div className="pm-muted pm-small">highest: {m.peakRestaurant.name}</div>}
                      </td>
                      <td>{limitText(m)}</td>
                      <td><Meter pct={m.pct} /></td>
                      <td><Badge tone={STATE_TONE[m.state]}>{STATE_LABEL[m.state]}</Badge></td>
                    </tr>
                    {open[m.key] && m.perRestaurant.map((r) => (
                      <tr key={`${m.key}-${r.cafeId}`} className="pu-subrow">
                        <td>↳ {r.name}</td>
                        <td>{fmt(m.unit, r.used)}</td>
                        <td>{limitText(m)}</td>
                        <td><Meter pct={m.max === null ? null : m.max === 0 ? (r.used > 0 ? 101 : 0) : Math.round(((r.used || 0) / m.max) * 100)} /></td>
                        <td />
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <h4 className="pu-h">This period</h4>
          <div className="pm-table-wrap">
            <table className="pm-table pu-table">
              <thead><tr><th>Quota</th><th>Used</th><th>Included</th><th>Usage</th><th>Resets</th></tr></thead>
              <tbody>
                {data.quotas.map((q) => (
                  <tr key={q.key}>
                    <td>{q.label}</td>
                    <td>{fmt(q.unit, q.used)}</td>
                    <td>{q.unlimited ? 'Unlimited' : fmt(q.unit, q.max)}</td>
                    <td><Meter pct={q.pct} /></td>
                    <td>{q.periodEnd ? formatDateTime(q.periodEnd) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
};

// ─── List ────────────────────────────────────────────────────────────────────
const PAGE = 25;

const PartnerUsage = ({ notify }) => {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [attention, setAttention] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const timer = useRef(null);

  // Debounce typing
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setQuery(q.trim()); setPage(1); }, 350);
    return () => clearTimeout(timer.current);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await planApi.usageList({ q: query || undefined, attention: attention ? 1 : undefined, page, limit: PAGE }));
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, [query, attention, page]);
  useEffect(() => { load(); }, [load]);

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <section className="pm-card">
      <div className="pu-toolbar">
        <div>
          <h3>Partner usage</h3>
          <p className="pm-muted pm-small">Everything each partner uses against their plan. Updated on every change and re-checked by the usage-recount job.</p>
        </div>
        <div className="pu-toolbar__controls">
          <input className="pm-input" type="search" placeholder="Search email, name, phone or ID" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search partners" />
          <label className="pm-check"><input type="checkbox" checked={attention} onChange={(e) => { setAttention(e.target.checked); setPage(1); }} /> Near or over a limit</label>
        </div>
      </div>

      {error && <div className="pm-callout pm-callout--error">{error} <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={load}>Retry</button></div>}

      <div className="pm-table-wrap">
        <table className="pm-table pu-table">
          <thead><tr><th>Partner</th><th>Plan</th><th>Restaurants</th><th>Needs attention</th><th>Last recount</th><th /></tr></thead>
          <tbody>
            {loading && !data && <tr><td colSpan={6} className="pm-muted">Loading…</td></tr>}
            {data && data.rows.length === 0 && (
              <tr><td colSpan={6} className="pm-muted">{attention ? 'No partner is near a limit.' : 'No partners found.'}</td></tr>
            )}
            {data?.rows.map((r) => (
              <tr key={r.ownerId} className="pu-row" onClick={() => setSelected(r.ownerId)}>
                <td>
                  <div>{r.name || r.email}</div>
                  {r.name && <div className="pm-muted pm-small">{r.email}</div>}
                </td>
                <td>{r.plan ? <>{r.plan.name} <Badge status={r.plan.status} /></> : <span className="pm-muted">No plan</span>}</td>
                <td>{r.restaurants}</td>
                <td>
                  {r.attention.length === 0 ? <Badge tone="good">OK</Badge> : (
                    <div className="pu-chips">
                      {r.attention.slice(0, 3).map((m) => (
                        <span key={m.key} className={`pu-chip pu-chip--${STATE_TONE[m.state]}`} title={m.peakRestaurant ? `Highest at ${m.peakRestaurant.name}` : undefined}>
                          {m.label} {fmt(m.unit, m.used)}/{fmt(m.unit, m.max)}
                        </span>
                      ))}
                      {r.attention.length > 3 && <span className="pm-muted pm-small">+{r.attention.length - 3} more</span>}
                    </div>
                  )}
                </td>
                <td className="pm-small">{r.lastRecountAt ? formatDateTime(r.lastRecountAt) : <span className="pm-muted">Not yet</span>}</td>
                <td><button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={(e) => { e.stopPropagation(); setSelected(r.ownerId); }}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE && (
        <div className="pu-pager">
          <button className="pm-btn pm-btn--ghost pm-btn--sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span className="pm-small">Page {page} of {pages} · {data.total} partners</span>
          <button className="pm-btn pm-btn--ghost pm-btn--sm" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}

      {selected && <UsageDetail ownerId={selected} notify={notify} onClose={() => setSelected(null)} onRecounted={load} />}
    </section>
  );
};

export default PartnerUsage;
